import React, { useState } from 'react';
import styled from 'styled-components';
import { FaUserGraduate, FaGift, FaIdCard, FaExclamationTriangle, FaEnvelope, FaPhoneAlt } from 'react-icons/fa';
import {
  Container,
  Section,
  PageHero,
  HeroArt,
  Reveal,
  Kicker,
  Card,
  StatusPill,
  ButtonPrimary,
  hudCompactControl,
} from '../components/ui';
import { FEES, CONFERENCE } from '../data/conference';

/* delegate-badge motif — a copper conference pass on a lanyard */
const RegistrationArt = (
  <HeroArt viewBox="0 0 320 260" aria-hidden="true">
    {/* lanyard */}
    <path className="frame soft" d="M120 8 L156 96" />
    <path className="frame soft" d="M196 8 L164 96" />
    {/* badge body */}
    <rect className="frame" x="96" y="96" width="128" height="150" rx="10" />
    <line className="frame faint" x1="96" y1="132" x2="224" y2="132" />
    {/* photo + lines */}
    <rect className="frame soft" x="116" y="150" width="44" height="48" rx="4" />
    <circle className="frame soft" cx="138" cy="166" r="9" />
    <path className="frame soft" d="M124 192 a14 12 0 0 1 28 0" />
    <line className="frame soft" x1="174" y1="156" x2="206" y2="156" />
    <line className="frame soft" x1="174" y1="172" x2="206" y2="172" />
    <line className="frame soft" x1="174" y1="188" x2="198" y2="188" />
    {/* barcode */}
    <g className="frame" strokeWidth="2">
      <line x1="116" y1="214" x2="116" y2="230" />
      <line x1="122" y1="214" x2="122" y2="230" />
      <line x1="130" y1="214" x2="130" y2="230" />
      <line x1="136" y1="214" x2="136" y2="230" />
      <line x1="146" y1="214" x2="146" y2="230" />
      <line x1="152" y1="214" x2="152" y2="230" />
    </g>
    {/* hex seal */}
    <polygon className="frame" points="196,206 214,216 214,236 196,246 178,236 178,216" />
    <circle className="node" cx="196" cy="226" r="4" />
    <circle className="node" cx="158" cy="96" r="4" />
  </HeroArt>
);

/* ----- Interactive fee calculator ----- */
const CalcPanel = styled.div`
  border: 1px solid var(--line-strong);
  border-radius: calc(var(--radius) + 4px);
  background: radial-gradient(130% 160% at 90% -30%, rgba(224, 141, 60, 0.13), var(--bg-card) 55%);
  padding: clamp(1.9rem, 4vw, 3rem);
  display: grid;
  grid-template-columns: 1.25fr 1fr;
  gap: clamp(1.8rem, 4vw, 3.5rem);
  margin-bottom: clamp(3rem, 6vw, 4.5rem);
  align-items: center;

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

const CalcControls = styled.div`
  display: grid;
  gap: 1.5rem;

  .group-label {
    font-family: var(--font-mono);
    font-size: 0.66rem;
    font-weight: 600;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--ink-faint);
    margin-bottom: 0.6rem;
  }
`;

const SegmentRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const SegmentBtn = styled.button`
  ${hudCompactControl}
  --compact-cut: 7px;
  --compact-rim: ${(p) =>
    p.$on
      ? 'linear-gradient(110deg, #e4922d, #5c3b1e 45%, #d57a20)'
      : 'linear-gradient(110deg, #6f777e, #262c32 48%, #747c83)'};
  --compact-surface: ${(p) =>
    p.$on
      ? 'linear-gradient(180deg, rgba(48, 29, 12, 0.98), rgba(17, 13, 9, 0.99))'
      : 'linear-gradient(180deg, #ffffff, #ffffff)'};
  font-family: var(--font-mono);
  font-size: 0.74rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.65rem 1.2rem;
  min-height: 42px;
  color: ${(p) => (p.$on ? 'var(--copper-bright)' : 'var(--ink-dim)')};
  transition: all 0.22s var(--ease-out);

  &:hover {
    color: var(--copper-bright);
    filter: drop-shadow(0 0 9px rgba(224, 141, 60, 0.2));
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const PriceReadout = styled.div`
  text-align: center;
  border: 1px dashed rgba(224, 141, 60, 0.45);
  border-radius: var(--radius);
  padding: clamp(1.8rem, 4vw, 2.6rem) 1.4rem;
  background: rgba(236, 233, 225, 0.45);

  .label {
    font-family: var(--font-mono);
    font-size: 0.64rem;
    letter-spacing: 0.24em;
    text-transform: uppercase;
    color: var(--ink-faint);
    margin-bottom: 0.9rem;
  }

  .price {
    font-family: var(--font-display);
    font-variant-numeric: tabular-nums;
    font-size: clamp(2.6rem, 6vw, 4rem);
    font-weight: 800;
    line-height: 1;
    background: linear-gradient(115deg, var(--copper-bright), var(--copper-deep));
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 0.8rem;
  }

  .meta {
    font-family: var(--font-mono);
    font-size: 0.7rem;
    letter-spacing: 0.1em;
    color: var(--ink-dim);
    margin-bottom: 1.5rem;
  }
`;

function FeeCalculator() {
  const [category, setCategory] = useState(0);
  const [phase, setPhase] = useState(0); // 0 = early bird, 1 = late
  const [ieee, setIeee] = useState(false);

  const row = FEES.rows[category];
  const price = ieee ? row.values[2] : row.values[phase];
  const phaseLabel = ieee ? 'IEEE Member rate' : phase === 0 ? 'Early Bird' : 'Late Registration';

  return (
    <CalcPanel>
      <CalcControls>
        <div>
          <p className="group-label">01 · Your category</p>
          <SegmentRow role="radiogroup" aria-label="Registration category">
            {FEES.rows.map((r, i) => (
              <SegmentBtn
                key={r.category}
                $on={category === i}
                onClick={() => setCategory(i)}
                role="radio"
                aria-checked={category === i}
              >
                {r.category}
              </SegmentBtn>
            ))}
          </SegmentRow>
        </div>
        <div>
          <p className="group-label">02 · Registration phase</p>
          <SegmentRow role="radiogroup" aria-label="Registration phase">
            <SegmentBtn $on={!ieee && phase === 0} onClick={() => setPhase(0)} disabled={ieee} role="radio" aria-checked={!ieee && phase === 0}>
              Early Bird
            </SegmentBtn>
            <SegmentBtn $on={!ieee && phase === 1} onClick={() => setPhase(1)} disabled={ieee} role="radio" aria-checked={!ieee && phase === 1}>
              Late
            </SegmentBtn>
          </SegmentRow>
        </div>
        <div>
          <p className="group-label">03 · IEEE membership</p>
          <SegmentRow>
            <SegmentBtn $on={ieee} onClick={() => setIeee((v) => !v)} aria-pressed={ieee}>
              {ieee ? 'IEEE Member ✓' : 'I am an IEEE Member'}
            </SegmentBtn>
          </SegmentRow>
        </div>
      </CalcControls>

      <PriceReadout aria-live="polite">
        <p className="label">Your estimated fee</p>
        <p className="price">{price}</p>
        <p className="meta">
          {row.category} · {phaseLabel}
          {row.category !== 'Foreign Delegates' && ' · incl. 18% GST'}
        </p>
        <ButtonPrimary as="span" $disabled aria-disabled="true">
          Register — Portal Soon
        </ButtonPrimary>
      </PriceReadout>
    </CalcPanel>
  );
}

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.1rem;
  margin-bottom: clamp(3.5rem, 7vw, 5.5rem);

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled(Card)`
  .icon {
    width: 50px;
    height: 50px;
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    color: var(--copper-bright);
    background: var(--copper-glow);
    border: 1px solid rgba(224, 141, 60, 0.28);
    margin-bottom: 1.4rem;
  }

  h2 {
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 1rem;
  }

  dl {
    display: grid;
    gap: 0.85rem;
  }

  dt {
    color: var(--ink);
    font-weight: 600;
    font-size: 0.95rem;
  }

  dd {
    color: var(--ink-dim);
    font-size: 0.92rem;
    line-height: 1.6;
  }

  ul {
    list-style: none;

    li {
      position: relative;
      padding: 0.36rem 0 0.36rem 1.5rem;
      color: var(--ink-dim);
      font-size: 0.93rem;
      line-height: 1.6;

      &::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: var(--plasma);
        font-size: 0.8rem;
      }
    }
  }
`;

/* ----- Fee matrix ----- */
const FeeWrap = styled.div`
  border: 1px solid var(--line-strong);
  border-radius: var(--radius);
  overflow: hidden;
  background: var(--bg-card);
`;

const FeeScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const FeeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 640px;

  caption {
    text-align: left;
    padding: 1.6rem 1.8rem 0.4rem;
    font-family: var(--font-mono);
    font-size: 0.7rem;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }

  th,
  td {
    padding: 1.15rem 1.8rem;
    text-align: left;
    border-bottom: 1px solid var(--line);
  }

  thead th {
    font-family: var(--font-mono);
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--copper-bright);
    border-bottom: 1px solid var(--line-strong);
    white-space: nowrap;
  }

  tbody th {
    font-family: var(--font-body);
    font-weight: 600;
    color: var(--ink);
    white-space: nowrap;
  }

  tbody td {
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    font-size: 1.02rem;
    color: var(--ink-dim);
  }

  tbody tr {
    transition: background 0.2s;

    &:hover {
      background: rgba(224, 141, 60, 0.05);

      td {
        color: var(--ink);
      }
    }

    &:last-child th,
    &:last-child td {
      border-bottom: none;
    }
  }
`;

const FeeNote = styled.p`
  font-family: var(--font-mono);
  font-size: 0.74rem;
  letter-spacing: 0.06em;
  color: var(--ink-faint);
  padding: 1.1rem 1.8rem;
  border-top: 1px solid var(--line);
`;

const NoticeBanner = styled.div`
  display: flex;
  gap: 1.2rem;
  border: 1px solid rgba(224, 141, 60, 0.4);
  border-radius: var(--radius);
  background: linear-gradient(115deg, rgba(224, 141, 60, 0.12), transparent 70%);
  padding: clamp(1.6rem, 3.5vw, 2.2rem);
  margin: clamp(3rem, 6vw, 4.5rem) 0;

  svg {
    color: var(--copper-bright);
    font-size: 1.4rem;
    flex: none;
    margin-top: 0.25rem;
  }

  h2 {
    font-size: 1.15rem;
    font-weight: 700;
    margin-bottom: 0.6rem;
  }

  p {
    color: var(--ink-dim);
    line-height: 1.7;
    font-size: 0.96rem;
  }
`;

const QueriesCard = styled.div`
  text-align: center;
  border: 1px dashed var(--line-strong);
  border-radius: var(--radius);
  padding: clamp(2rem, 5vw, 3rem);

  h2 {
    font-size: 1.4rem;
    font-weight: 700;
    margin-bottom: 1.4rem;
  }

  .rows {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1rem 2.6rem;
    color: var(--ink-dim);
    font-size: 0.96rem;

    span {
      display: inline-flex;
      align-items: center;
      gap: 0.6rem;
    }

    svg {
      color: var(--copper);
      font-size: 0.85rem;
    }

    a {
      color: var(--ink-dim);
      transition: color 0.2s;

      &:hover {
        color: var(--copper-bright);
      }
    }
  }

  .hours {
    margin-top: 1.3rem;
    font-family: var(--font-mono);
    font-size: 0.72rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
`;

export default function Registration() {
  return (
    <>
      <PageHero
        kicker="Registration"
        index="05"
        title={
          <>
            Reserve your seat at the <em style={{ fontStyle: 'normal', color: 'var(--copper-bright)' }}>10th edition.</em>
          </>
        }
        lead="Join IEMENTech 2027 — register for the premier conference on Electronics, Materials Engineering & Nano-Technology."
        art={RegistrationArt}
      >
        <div style={{ marginTop: '1.8rem' }}>
          <StatusPill color="copper">Registration portal — opening soon</StatusPill>
        </div>
      </PageHero>

      <Section>
        <Container>
          <Reveal>
            <Kicker index="A">Estimate your fee</Kicker>
            <FeeCalculator />
          </Reveal>

          <Reveal>
            <Kicker index="B">Before you register</Kicker>
          </Reveal>
          <InfoGrid>
            <Reveal>
              <InfoCard as="article" style={{ height: '100%' }}>
                <div className="icon">
                  <FaUserGraduate />
                </div>
                <h2>Registration Categories</h2>
                <dl>
                  <div>
                    <dt>Student Registration</dt>
                    <dd>For undergraduate and postgraduate students with valid student ID</dd>
                  </div>
                  <div>
                    <dt>Faculty Registration</dt>
                    <dd>For academic faculty members and researchers</dd>
                  </div>
                  <div>
                    <dt>Industry Registration</dt>
                    <dd>For industry professionals and corporate participants</dd>
                  </div>
                  <div>
                    <dt>International Registration</dt>
                    <dd>For participants from outside India</dd>
                  </div>
                </dl>
              </InfoCard>
            </Reveal>

            <Reveal delay={80}>
              <InfoCard as="article" style={{ height: '100%' }}>
                <div className="icon">
                  <FaGift />
                </div>
                <h2>Registration Benefits</h2>
                <ul>
                  <li>Access to all keynote sessions and presentations</li>
                  <li>Conference kit with proceedings and materials</li>
                  <li>Networking opportunities with industry experts</li>
                  <li>Certificate of participation</li>
                  <li>Refreshments and lunch during conference days</li>
                  <li>Access to exhibition and poster sessions</li>
                </ul>
              </InfoCard>
            </Reveal>

            <Reveal delay={160}>
              <InfoCard as="article" style={{ height: '100%' }}>
                <div className="icon">
                  <FaIdCard />
                </div>
                <h2>Required Documents</h2>
                <dl>
                  <div>
                    <dt>For Students</dt>
                    <dd>Valid student ID card · Institution verification letter</dd>
                  </div>
                  <div>
                    <dt>For Faculty / Industry</dt>
                    <dd>Professional ID or business card · Institutional affiliation proof</dd>
                  </div>
                  <div>
                    <dt>For International Participants</dt>
                    <dd>Passport copy · Visa documentation (if required)</dd>
                  </div>
                </dl>
              </InfoCard>
            </Reveal>
          </InfoGrid>

          <Reveal>
            <Kicker index="C">Full Fee Matrix</Kicker>
            <FeeWrap>
              <FeeScroll>
                <FeeTable>
                  <caption>Fee matrix — all categories</caption>
                  <thead>
                    <tr>
                      <th scope="col">Category</th>
                      {FEES.columns.map((col) => (
                        <th scope="col" key={col}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {FEES.rows.map((row) => (
                      <tr key={row.category}>
                        <th scope="row">{row.category}</th>
                        {row.values.map((v, i) => (
                          <td key={i}>{v}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </FeeTable>
              </FeeScroll>
              <FeeNote>{FEES.note}</FeeNote>
            </FeeWrap>
          </Reveal>

          <Reveal>
            <NoticeBanner role="note">
              <FaExclamationTriangle aria-hidden="true" />
              <div>
                <h2>Important Registration Information</h2>
                <p>
                  Registration will open soon! Stay tuned for the official registration portal launch. Early bird
                  discount dates will be announced shortly. All registrations include conference materials, meals,
                  and certificate of participation. Payment can be made online through secure payment gateway or
                  bank transfer.
                </p>
              </div>
            </NoticeBanner>
          </Reveal>

          <Reveal>
            <QueriesCard>
              <h2>Registration Queries</h2>
              <div className="rows">
                <span>
                  <FaEnvelope aria-hidden="true" />
                  <a href={`mailto:${CONFERENCE.email}`}>{CONFERENCE.email}</a>
                </span>
                <span>
                  <FaPhoneAlt aria-hidden="true" />
                  {CONFERENCE.phones[0]}
                </span>
                <span>
                  <FaPhoneAlt aria-hidden="true" />
                  {CONFERENCE.phones[1]}
                </span>
              </div>
              <p className="hours">Office hours · Monday to Friday · 9:00 AM – 5:00 PM</p>
            </QueriesCard>
          </Reveal>
        </Container>
      </Section>
    </>
  );
}
